<template>
    <div class="gift-form-container">
        <el-form ref="formRef" :model="formData" label-width="120px" :rules="rules">
            <el-form-item label="年月"  prop="datatime">
                <el-date-picker
                    v-model="formData.datatime"
                    type="month"
                    placeholder="选择年月"
                    format="YYYY-MM"
                    value-format="YYYY-MM"
                    style="width: 100%">
                </el-date-picker>
            </el-form-item>

            <el-form-item label="标题" prop="title">
                <el-input v-model="formData.title" placeholder="请输入标题"></el-input>
            </el-form-item>

            <el-form-item label="内容" prop="content">
                <el-input type="textarea" v-model="formData.content" :rows="4" placeholder="请输入内容"></el-input>
            </el-form-item>

            <el-form-item label="舰长填写地址" prop="address_1">
                <el-input v-model="formData.address_1" placeholder="请输入舰长表单链接"></el-input>
            </el-form-item>

            <el-form-item label="提督填写地址" prop="address_2">
                <el-input v-model="formData.address_2" placeholder="请输入提督表单链接"></el-input>
            </el-form-item>

            <el-form-item label="总督填写地址" prop="address_3">
                <el-input v-model="formData.address_3" placeholder="请输入总督表单链接"></el-input>
            </el-form-item>

            <el-divider>礼物图片</el-divider>

            <el-form-item v-for="i in 5" :key="i" :label="'礼物图片 ' + i" class="image-upload-item">
                <el-upload 
                    action="#" 
                    :show-file-list="false" 
                    :before-upload="(file) => beforeImageUpload(i, file)"
                    accept="image/*">
                    <div v-if="formData[`image_${i}`]" class="image-container">
                        <el-image :src="getImageUrl(formData[`image_${i}`])" fit="cover" class="uploaded-image" />
                        <div class="image-overlay">
                            <el-icon :size="20" @click.stop="handleRemoveImage(i)">
                                <Delete />
                            </el-icon>
                        </div>
                    </div>
                    <el-icon v-else class="upload-icon">
                        <Plus />
                    </el-icon>
                </el-upload>
            </el-form-item>

            <el-form-item>
                <el-button type="primary" @click="submitForm" :loading="loading">提交</el-button>
                <el-button @click="resetForm">重置</el-button>
            </el-form-item>
        </el-form>
    </div>
</template>

<script setup>
import { ref, reactive } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Delete, Plus } from '@element-plus/icons-vue';
import axios from 'axios';

const formRef = ref(null);
const loading = ref(false);
const imageMimeTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
// 确保始终返回格式化的日期
const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
};
// 初始化包含所有图片字段的默认对象
const createDefaultFormData = () => ({
    datatime: formatDate(new Date()),
    title: '',
    content: '',
    address_1: '',
    address_2: '',
    address_3: '',
    image_1: null,
    image_2: null,
    image_3: null,
    image_4: null,
    image_5: null
});

const formData = reactive(createDefaultFormData());

const rules = reactive({
    datatime: [
        { required: true, message: '请输入日期', trigger: 'blur' }
    ],
    title: [
        { required: true, message: '请输入标题', trigger: 'blur' },
        { min: 2, max: 50, message: '标题长度在 2 到 50 个字符', trigger: 'blur' }
    ],
    content: [
        { required: true, message: '请输入内容', trigger: 'blur' }
    ]
});



// 统一处理图片URL获取
const getImageUrl = (path) => {
    if (!path) return '';
    return path instanceof File ? URL.createObjectURL(path) : path;
};

// 图片上传处理
const beforeImageUpload = (index, file) => {
    if (!imageMimeTypes.includes(file.type)) {
        ElMessage.error('只能上传图片文件 (JPG, PNG, GIF, WEBP)');
        return false;
    }
    if (file.size > 10 * 1024 * 1024) {
        ElMessage.error('图片大小不能超过10MB');
        return false;
    }
    formData[`image_${index}`] = file;
    return false;
};

// 图片删除处理
const handleRemoveImage = (index) => {
    ElMessageBox.confirm('确定要删除这张图片吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
    }).then(() => {
        formData[`image_${index}`] = null; // 设置为null而不是删除字段
        ElMessage.success('图片已移除');
    }).catch(() => {});
};
const submitForm = async () => {
    try {
        await formRef.value.validate();
        
        const formDataObj = new FormData();
        
        // 确保所有字段都被包含，特别是image_1到image_5
        Object.keys(formData).forEach(key => {
            // 对于图片字段，确保1-5全部包含
            if (key.startsWith('image_')) {
                // 如果是文件对象，直接添加
                if (formData[key] instanceof File) {
                    formDataObj.append(key, formData[key]);
                } 
                // 如果是字符串路径（已有图片）
                else if (typeof formData[key] === 'string' && formData[key]) {
                    formDataObj.append(key, formData[key]);
                }
                // 如果是null或undefined，显式添加空值
                else {
                    formDataObj.append(key, '');
                }
            }
            // 非图片字段
            else {
                formDataObj.append(key, formData[key] || '');
            }
        });

        loading.value = true;
        const response = await axios.post('/adminapi/gifts/add', formDataObj, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        
        ElMessage.success('礼物添加成功');
        resetForm();
    } catch (error) {
        console.error('提交失败:', error);
        ElMessage.error(error.response?.data?.message || '操作失败');
    } finally {
        loading.value = false;
    }
};

// 重置表单时确保所有图片字段都存在
const resetForm = () => {
    Object.assign(formData, createDefaultFormData());
    formRef.value?.resetFields();
};
</script>

<style scoped>
.gift-form-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
}

.image-upload-item {
    margin-bottom: 20px;
}

.upload-icon {
    font-size: 28px;
    color: #8c939d;
    width: 120px;
    height: 120px;
    line-height: 120px;
    text-align: center;
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
    transition: border-color 0.3s;
}

.upload-icon:hover {
    border-color: #409eff;
}

.image-container {
    position: relative;
    width: 120px;
    height: 120px;
    overflow: hidden;
    border-radius: 6px;
}

.uploaded-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s;
    cursor: pointer;
}

.image-container:hover .image-overlay {
    opacity: 1;
}

.image-overlay .el-icon {
    color: white;
    font-size: 20px;
}
</style>